<script setup lang="ts">
import { useTruncate } from '~/js/composables/truncate'
import { Location } from '~/js/interfaces/locations'
import { getClasses } from '~/js/composables/classes'
import { getGlobalTranslation } from '~/js/composables/translations'

//import BadgePrimary from '~/vue/atoms/badges/BadgePrimary.vue'
//import BadgeSecondary from '~/vue/atoms/badges/BadgeSecondary.vue'
//import ButtonTertiary from '~/vue/atoms/buttons/ButtonTertiary.vue'
import ImageTeaser from '~/vue/atoms/images/ImageTeaser.vue'
import LinkOverlay from '~/vue/atoms/links/LinkOverlay.vue'
import TextH2 from '~/vue/atoms/texts/TextH2.vue'
import TextH3 from '~/vue/atoms/texts/TextH3.vue'
import TextProse from '~/vue/atoms/texts/TextProse.vue'
import TextStatus from '~/vue/atoms/texts/TextStatus.vue'

interface Props {
    card: Location,
    utilities?: string,
    theme?: string|null,
}

withDefaults(defineProps<Props>(), {
    utilities: '',
    theme: null,
})

const classes = {
    wrapper: {
        _default: 'relative group/card-location overflow-hidden',
        animation: 'transition-colors',
        border: 'border border-neutral-100',
        color: 'bg-white',
        flex: 'flex flex-col',
        focus: 'focus-within:ring-2 focus-within:ring-offset-white dark:ring-offset-black focus-within:ring-offset-2 focus-within:outline-none focus-within:ring-offset-red-light',
        shadow: 'shadow-sm',
        size: 'w-full h-full',
    },
    content: {
        spacing: 'p-4 space-y-4',
        flex: 'flex flex-col grow',
    },
    badges: {
        flex: 'flex flex-wrap gap-2',
    },
}

const getColor = (status: string): { background: string; text: string } => {
    const colors: Record<string, { background: string; text: string }> = {
        'available': {
            'background': 'bg-green-500',
            'text': 'text-blue-500',
        },
        'rented': {
            'background': 'bg-blue-500',
            'text': 'text-white',
        },
        'project': {
            'background': 'bg-blue-200',
            'text': 'text-blue-500',
        }
    }

    return colors[status] || colors['available']
}

const getIcon = (part: string): string => {
    const icons: Record<string, string> = {
        'warehouse': 'fa-regular fa-warehouse-full',
        'office': 'fa-regular fa-chair-office',
        'bureaux': 'fa-regular fa-chair-office',
        'kantoorruimte': 'fa-regular fa-chair-office',
        'plot': 'fa-regular fa-ruler-combined',
        'default': 'fa-regular fa-warehouse',
    }

    return icons[part] || icons['default']
}

</script>

<template>
    <article
        :class="[
            getClasses(classes,'wrapper'),
            utilities
        ]"
    >
        <ImageTeaser
            v-if="card.teaser_image"
            :image="card.teaser_image"
            :sizes="['25vw']"
            ratio="aspect-[3/2] md:aspect-video"
        />

        <div :class="`${getColor(card.status_code).background} w-full p-2 text-center`">
            <TextStatus 
                :color="getColor(card.status_code).text"
            >
                {{ getGlobalTranslation(card.status) }}
            </TextStatus>
        </div>

        <div :class="getClasses(classes, 'content')">

            <div class="space-y-1">
                <h1>
                    <TextH2 
                        :theme="theme"
                    >
                        {{ card.title }}
                    </TextH2>
                </h1>

                <h2>
                    <TextH3 
                        :theme="theme"
                    >
                        {{ card.address_title || card.address_line_1 }}
                    </TextH3>
                </h2>
            </div>

            <TextProse 
                color="text-neutral-700"
                utilities="block"
            >
                <span v-html="useTruncate(`<p>${card.description}</p>`, { limit: 120, by: 'chars', ellipsis: true })"/>
            </TextProse>

            <div v-if="card.building_parts.length > 0" class="border-t border-gray-900 space-y-4 mt-auto pt-4 pb-2">
                <div v-for="building_part in card.building_parts" class="space-x-4 flex flex-nowrap">
                    <span class="inline-flex text-blue-300 text-xl w-8 h-auto">
                        <i :class="getIcon(building_part.part.toLowerCase())"></i>
                    </span>
                    <div class="inline-flex flex-nowrap text-sm font-bold gap-x-1">
                        <span>{{ building_part.surface }} m<sup>2</sup></span>
                        <span v-if="building_part.surfaceMax">
                            &mdash; {{ building_part.surfaceMax }} m<sup>2</sup>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <LinkOverlay
            :ga="{
                category: 'card-warehouse',
                action: 'click',
                label: card.title
            }"
            :title="card.title"
            :url="card.url"
        />
    </article>
</template>